import { BaseValidation, Validation } from '@/components/datamodel/types';
import { getNestedFormPathsFromDataModelPath } from '@/components/datamodel/hooks/attributeFieldPathParser.shared';

interface PreparedValidationEntry {
    path: string;
    kind: 'error' | 'warning';
    validation: Validation;
}

interface PreparedValidationOperations {
    clearAll: boolean;
    attributeNamesToReset: string[];
    entries: PreparedValidationEntry[];
}

const prepareValidationOperations = ({
    validations,
    validatedAttributeNames,
}: {
    validations: Record<string, BaseValidation[]>;
    validatedAttributeNames?: string[];
}): PreparedValidationOperations => {
    const validationAttributeNames = Object.keys(validations);

    if (validationAttributeNames.length === 0) {
        return {
            // Only clear server validation errors/warnings, keep local validation errors
            clearAll: !validatedAttributeNames || validatedAttributeNames.length === 0,
            attributeNamesToReset: validatedAttributeNames ?? [],
            entries: [],
        };
    }

    // Always reset attributes that have new validations in the response
    const attributeNamesToReset = new Set<string>(validationAttributeNames);

    // Clear server validation errors for attributes that were validated but are not in the response
    // (meaning they're now valid on server). Note: Local validation errors are not cleared - they persist independently
    validatedAttributeNames?.forEach((attributeName) => {
        if (!validationAttributeNames.includes(attributeName)) {
            attributeNamesToReset.add(attributeName);
        }
    });

    const entries = validationAttributeNames.flatMap((attributeName) => {
        const attributeValidations = validations[attributeName] ?? [];

        // If attribute has an empty array, server says it's valid (no server errors)
        // But we don't clear local validation errors - they persist independently
        return attributeValidations.flatMap((validation) => {
            const kind: PreparedValidationEntry['kind'] =
                validation.level === 'Failure' || validation.level === 'Error'
                    ? 'error'
                    : 'warning';

            // From the validation path we have, remove 'composite' from it, and get
            // an array of all the paths of the invalid attribute. This way, we can
            // even mark deeply nested attributes in composites.
            return getNestedFormPathsFromDataModelPath(validation.path).map((path) => ({
                path,
                kind,
                validation: {
                    attributeName,
                    ...validation,
                },
            }));
        });
    });

    return {
        clearAll: false,
        attributeNamesToReset: Array.from(attributeNamesToReset),
        entries,
    };
};

interface ValidationWorkerApi {
    prepareValidationOperations: (args: {
        validations: Record<string, BaseValidation[]>;
        validatedAttributeNames?: string[];
    }) => PreparedValidationOperations;
}

export {
    prepareValidationOperations,
    type PreparedValidationOperations,
    type ValidationWorkerApi,
};
